'use client'

import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'
import React from 'react'

import type { AvatarSize } from '~/core/ui/Avatar'
import { Avatar } from '~/core/ui/Avatar'
import type { IDotColorProps } from '~/core/ui/Dot'
import { Dot } from '~/core/ui/Dot'
import type { LucideIconName } from '~/core/ui/IconWrapper'
import IconWrapper from '~/core/ui/IconWrapper'
import { Tooltip } from '~/core/ui/Tooltip'
import { cn } from '~/core/ui/utils'
import { TooltipContentKey } from './SuggestionChips'

const inputChipVariants = cva(
  'focus:ring-primary-300 dark:focus:ring-primary-700 flex items-center bg-gray-100 hover:bg-gray-300 focus:ring-1 dark:bg-gray-700 dark:hover:bg-gray-600',
  {
    variants: {
      variant: {
        4: 'pl-3',
        3: 'pl-2.5',
        2: 'pl-2',
        1: 'pl-1.5',
        0.5: 'pl-0.5'
      },
      size: {
        lg: 'h-7 rounded-2xl py-0.5 pr-3',
        md: 'h-5 rounded-xl py-0.5 pr-2.5',
        sm: 'h-5 rounded-xl pr-2'
      },
      dragged: {
        default: '',
        dragged: 'shadow'
      }
    },
    defaultVariants: {
      variant: 4,
      size: 'lg',
      dragged: 'default'
    }
  }
)

const inputChipLabelVariants = cva('font-medium', {
  variants: {
    size: {
      lg: 'text-base',
      md: 'text-sm',
      sm: 'text-sm'
    }
  },
  defaultVariants: {
    size: 'lg'
  }
})

const inputChipMarginRightVariants = cva('', {
  variants: {
    size: {
      lg: 'mr-[6px]',
      md: 'mr-1',
      sm: 'mr-1'
    }
  },
  defaultVariants: {
    size: 'lg'
  }
})

const inputChipMarginLeftVariants = cva('-mr-0.5', {
  variants: {
    size: {
      lg: 'ml-[6px]',
      md: 'ml-1',
      sm: 'ml-1'
    }
  },
  defaultVariants: {
    size: 'lg'
  }
})

const getInputChipPaddingCondition = ({
  size = 'lg',
  type = 'default'
}: {
  size?: 'lg' | 'md' | 'sm'
  type?: 'default' | 'ava-leading' | 'dot-leading' | 'icon-leading'
}) => {
  if (size === 'lg' && type === 'default') return 4

  if (
    (size === 'md' && type === 'default') ||
    (size === 'lg' && ['dot-leading', 'icon-leading'].includes(type))
  ) {
    return 3
  }

  if (
    (size === 'sm' && type === 'default') ||
    (size === 'md' && ['dot-leading', 'icon-leading'].includes(type))
  ) {
    return 2
  }

  if (size === 'sm' && ['dot-leading', 'icon-leading'].includes(type)) return 1
  if (type === 'ava-leading') return 0.5

  return 4
}

const inputChipDefaultSizeAvatar: { [key: string]: AvatarSize } = {
  lg: 'sm',
  md: 'xs',
  sm: '2xs'
}
const inputChipDefaultSizeIcon: { [key: string]: number } = {
  lg: 16,
  md: 14,
  sm: 12
}

const getDescriptionText = (value?: string) => {
  if (!value) return ''

  const textWithLineBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')

  if (typeof document === 'undefined') {
    return textWithLineBreaks.trim()
  }

  const textarea = document.createElement('textarea')
  textarea.innerHTML = textWithLineBreaks

  return textarea.value.trim()
}

interface InputChipsProps {
  size?: 'lg' | 'md' | 'sm'
  type?: 'default' | 'ava-leading' | 'dot-leading' | 'icon-leading'
  removable?: boolean
  srcAvatar?: string
  altAvatar?: string
  colorAvatar?: string
  iconLeading?: LucideIconName
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  label?: string
  labelClassName?: string
  labelWrapperClassName?: string
  isDragged?: boolean
  color?: IDotColorProps
  buttonClassName?: string
  destructiveError?: boolean
  destructiveErrorText?: string | ReactNode
  ref?: React.Ref<HTMLButtonElement>
  description?: string
  showTooltip?: boolean
  tooltipContent?: TooltipContentKey[]
}

const InputChips = (props: InputChipsProps) => {
  const {
    size = 'lg',
    type = 'default',
    removable = false,
    srcAvatar,
    altAvatar,
    colorAvatar,
    iconLeading,
    onClick,
    label,
    labelClassName,
    labelWrapperClassName,
    isDragged,
    color,
    buttonClassName = 'w-full',
    destructiveError,
    destructiveErrorText,
    showTooltip = false,
    tooltipContent = ['label'],
    description,
    ref
  } = props
  const renderTypeLeading = () => {
    if (type === 'ava-leading') {
      return (
        <span className={cn(inputChipMarginRightVariants({ size }))}>
          <Avatar
            color={colorAvatar}
            className="border border-solid border-white dark:border-gray-500"
            src={srcAvatar}
            alt={altAvatar}
            size={inputChipDefaultSizeAvatar[size]}
          />
        </span>
      )
    }

    if (type === 'dot-leading') {
      return (
        <Dot
          color={color}
          size="lg"
          className={cn(inputChipMarginRightVariants({ size }))}
        />
      )
    }

    if (type === 'icon-leading') {
      return (
        <span className={cn(inputChipMarginRightVariants({ size }))}>
          <IconWrapper
            name={iconLeading}
            size={inputChipDefaultSizeIcon[size]}
          />
        </span>
      )
    }

    return ''
  }
    const renderTooltipContent = () => {
      if (!showTooltip) return null
  
      const showLabel = tooltipContent.includes('label')
      const showDescription = tooltipContent.includes('description') || Boolean(description)
      const descriptionText = getDescriptionText(description)
      const shouldShowDescription = showDescription && Boolean(descriptionText)
  
      if (!showLabel && !shouldShowDescription) return null
  
      return (
        <div className="flex flex-col">
          {/* Label */}
          {showLabel && (
            <span className="font-medium text-sm">
              {label}
            </span>
          )}
  
          {/* Description */}
          {shouldShowDescription && (
            <span className="description-content whitespace-pre-line">
              {descriptionText}
            </span>
          )}
        </div>
      )
    }

  return (
    <Tooltip
      content={destructiveErrorText}
      classNameConfig={{
        content: destructiveError && destructiveErrorText ? '' : 'hidden'
      }}>
      <button
        type="button"
        ref={ref}
        className={cn(
          `${buttonClassName}`,
          inputChipVariants({
            variant: getInputChipPaddingCondition({
              size,
              type
            }),
            size,
            dragged: isDragged ? 'dragged' : 'default'
          })
        )}>
        {renderTypeLeading()}
        <Tooltip
          isCustomArrow
          content={renderTooltipContent()}>
          <span
            className={cn(
              'block truncate text-gray-700 dark:text-gray-100',
              inputChipLabelVariants({ size }),
              destructiveError ? 'text-red-500' : '',
              labelWrapperClassName,
              labelClassName
            )}>
            {label}
          </span>
        </Tooltip>

        {removable && (
          <span
            className={cn(inputChipMarginLeftVariants({ size }))}
            onClick={(event) => {
              if (onClick) {
                onClick(event)
              }
            }}>
            <IconWrapper
              name="X"
              className={destructiveError ? 'text-red-500' : ''}
              size={inputChipDefaultSizeIcon[size]}
            />
          </span>
        )}
      </button>
    </Tooltip>
  )
}

InputChips.displayName = 'InputChips'

export { InputChips }
export type { InputChipsProps }
